import { Clock, LifeBuoy } from 'lucide-react';
import { useAuth } from '@/shared/auth';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { useEndSupport } from './useEndSupport';
import { useSupportCountdown } from './useSupportCountdown';

/**
 * Steht während einer Support-Sitzung über jeder Seite und lässt sich nicht schließen: wer hier
 * arbeitet, handelt im Workspace eines Kunden. Es steht als rotes Band oben im Rahmen.
 */
export function SupportSessionBanner() {
  const { support } = useAuth();
  if (!support) return null;
  return (
    <ActiveSupportBanner workspaceName={support.workspaceName} expiresAt={support.expiresAt} />
  );
}

function ActiveSupportBanner({
  workspaceName,
  expiresAt,
}: {
  workspaceName: string;
  expiresAt: string;
}) {
  const time = useSupportCountdown(expiresAt);
  const { end, pending } = useEndSupport();
  return (
    <section
      aria-label="Support-Sitzung"
      className="bg-sealwax flex h-14 flex-none items-center gap-3 px-3 text-white sm:px-5"
    >
      <LifeBuoy size={18} strokeWidth={1.8} aria-hidden className="flex-none text-white/85" />
      <p className="flex min-w-0 flex-1 flex-col text-[13px] leading-tight sm:flex-row sm:items-center sm:gap-3">
        <span className="truncate" title={workspaceName}>
          <span className="text-white/80">Support-Sitzung in </span>
          <span className="font-semibold">{workspaceName}</span>
        </span>
        <span
          className={cn(
            'rounded-pill flex h-[30px] flex-none items-center gap-1 px-3 font-bold',
            time.warning ? 'bg-amber-on-dark text-ink' : 'bg-white/15 text-white',
          )}
        >
          {time.warning && <Clock size={13} aria-hidden />}
          {time.text}
        </span>
      </p>
      <span role="status" className="sr-only">
        {time.warning ? 'Die Support-Sitzung läuft in wenigen Minuten ab.' : ''}
      </span>
      <Button
        type="button"
        variant="ghost-dark"
        className="min-h-11 flex-none border border-white/40 text-white focus-visible:outline-white"
        loading={pending}
        onClick={end}
      >
        Beenden
      </Button>
    </section>
  );
}
